import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FiArrowLeft as ArrowLeft, FiCopy as Copy, FiCheck as Check, FiMessageSquare as MessageSquare, FiUser as User, FiClock as Clock,
  FiDollarSign as DollarSign, FiActivity as Activity, FiHash as Hash, FiAlertCircle as AlertCircle, FiChevronRight as ChevronRight
} from 'react-icons/fi';
import { getSession, getAgentRuns, assignSessionProject, assignSessionWorkload } from '../../../../services/aiOperationsApi';
import ProjectAssignmentSelect from '../../../../components/ProjectAssignmentSelect';
import WorkloadAssignmentSelect from '../../../../components/WorkloadAssignmentSelect';

const SessionDetailPage = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<Record<string, any> | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [runs, setRuns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('messages');
  const [loadingRuns, setLoadingRuns] = useState(false);

  useEffect(() => {
    loadSession();
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSession = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSession(sessionId!);
      setSession(data.session);
      setMessages(data.messages || []);
    } catch (err: unknown) {
      console.error('Error loading session:', err);
      setError((err instanceof Error ? err.message : String(err)) || 'Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const loadRuns = async () => {
    if (runs.length > 0) return; // Already loaded
    setLoadingRuns(true);
    try {
      const data = await getAgentRuns({ sessionId: session!.session_id });
      setRuns(data.runs || data.data || []);
    } catch (err: unknown) {
      console.error('Error loading runs:', err);
    } finally {
      setLoadingRuns(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'runs' && session) {
      loadRuns();
    }
  }, [activeTab, session]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleBack = () => {
    navigate('/ai-operations/observability?tab=sessions');
  };

  const formatTimestamp = (timestamp: string | undefined) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (startTime: string | undefined, endTime: string | undefined) => {
    if (!startTime || !endTime) return 'N/A';
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();

    if (durationMs < 1000) return `${durationMs}ms`;
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(2)}s`;
    return `${(durationMs / 60000).toFixed(2)}m`;
  };

  const getStatusColor = (status: string | undefined) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'completed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'abandoned':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const getRoleColor = (role: string | undefined) => {
    switch (role?.toLowerCase()) {
      case 'user':
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
      case 'assistant':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'system':
        return 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800';
      case 'tool':
        return 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800';
      default:
        return 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800';
    }
  };

  const getRoleBadgeColor = (role: string | undefined) => {
    switch (role?.toLowerCase()) {
      case 'user':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'assistant':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'system':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
      case 'tool':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center text-gray-600 dark:text-gray-400">
          Session not found
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900
                   dark:hover:text-white mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Sessions
        </button>

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <MessageSquare className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Session Details
              </h1>
            </div>

            <div className="flex items-center space-x-2 mt-2">
              <code className="text-sm bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded
                             text-gray-800 dark:text-gray-200">
                {session.session_id}
              </code>
              <button
                onClick={() => handleCopy(session.session_id, 'session_id')}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                title="Copy session ID"
              >
                {copiedField === 'session_id' ? (
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {session.agent_name && (
              <span className="px-3 py-1 text-sm rounded-full bg-purple-100 text-purple-800
                             dark:bg-purple-900/30 dark:text-purple-400">
                {session.agent_name}
              </span>
            )}
            <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(session.status)}`}>
              {session.status || 'Unknown'}
            </span>
          </div>
        </div>
      </div>

      {/* Overview Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200
                    dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex items-start space-x-3">
            <User className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">User ID</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {session.user_id || 'N/A'}
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Duration</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {formatDuration(session.started_at, session.ended_at)}
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <MessageSquare className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Messages</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {session.message_count || 0}
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Hash className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Total Tokens</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {session.total_tokens?.toLocaleString() || 0}
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <DollarSign className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Total Cost</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                ${session.total_cost?.toFixed(4) || '0.0000'}
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Activity className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Status</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {session.status || 'Unknown'}
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Started At</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {formatTimestamp(session.started_at)}
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Ended At</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {formatTimestamp(session.ended_at)}
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Activity className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Project</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                <ProjectAssignmentSelect
                  currentProjectId={session.project_id}
                  onAssign={async (projectId) => {
                    await assignSessionProject(session.id, projectId);
                    setSession((prev: any) => prev ? { ...prev, project_id: projectId } : prev);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Activity className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Workload</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                <WorkloadAssignmentSelect
                  currentWorkloadId={session.workload_id}
                  onAssign={async (workloadId) => {
                    await assignSessionWorkload(session.id, workloadId);
                    setSession((prev: any) => prev ? { ...prev, workload_id: workloadId } : prev);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200
                    dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'messages'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Messages ({messages.length})
            </button>
            <button
              onClick={() => setActiveTab('runs')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'runs'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Agent Runs {runs.length > 0 ? `(${runs.length})` : ''}
            </button>
            <button
              onClick={() => setActiveTab('metadata')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'metadata'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Metadata
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div className="space-y-4">
              {messages.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  No messages found for this session
                </div>
              ) : (
                messages.map((message, index) => (
                  <div
                    key={message.id || index}
                    className={`border rounded-lg p-4 ${getRoleColor(message.role)}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${getRoleBadgeColor(message.role)}`}>
                          {message.role}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatTimestamp(message.timestamp || message.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {message.tokens && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {message.tokens} tokens
                          </span>
                        )}
                        {message.cost && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            ${message.cost.toFixed(4)}
                          </span>
                        )}
                        <button
                          onClick={() => handleCopy(message.content, `message-${index}`)}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                        >
                          {copiedField === `message-${index}` ? (
                            <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
                          ) : (
                            <Copy className="w-3 h-3 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {message.content}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Agent Runs Tab */}
          {activeTab === 'runs' && (
            <div>
              {loadingRuns ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : runs.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  No agent runs found for this session
                </div>
              ) : (
                <div className="space-y-3">
                  {runs.map((run) => (
                    <Link
                      key={run.id}
                      to={`/ai-operations/runs/${run.id}`}
                      className="block border border-gray-200 dark:border-gray-700 rounded-lg p-4
                               hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {run.agent_name || 'Unknown Agent'}
                            </span>
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(run.status)}`}>
                              {run.status}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {run.task || 'No task description'}
                          </div>
                          <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                            <span className="flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {run.duration_seconds ? `${run.duration_seconds.toFixed(2)}s` : 'N/A'}
                            </span>
                            <span className="flex items-center">
                              <Hash className="w-3 h-3 mr-1" />
                              {run.steps_count || 0} steps
                            </span>
                            <span className="flex items-center">
                              <DollarSign className="w-3 h-3 mr-1" />
                              ${run.cost?.toFixed(4) || '0.0000'}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Metadata Tab */}
          {activeTab === 'metadata' && (
            <div>
              <pre className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto text-xs
                           text-gray-800 dark:text-gray-200">
                {JSON.stringify(session, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionDetailPage;
